const POLLING_INTERVAL_MS = 3000;

type RefetchQuery<TData> = { state: { data?: TData; error?: unknown } };

type RefetchIntervalOptions<TData> = {
  maxAttempts: number | undefined;
  maxDurationMs: number | undefined;
  keepPollingOnError: boolean;
  /**
   * When polling is finished for the given data. Defaults to "every value is truthy",
   * which fits the active-flag response shapes; list responses need their own predicate.
   */
  isDone?: (data: TData) => boolean;
  onStop?: () => void;
  getAttemptCount: () => number;
  getStartTime: () => number;
};

function everyValueTruthy(data: unknown) {
  const values = Object.values(data ?? {});
  return values.length > 0 && values.every(Boolean);
}

export function makeRefetchInterval<TData = unknown>({
  maxAttempts,
  maxDurationMs,
  keepPollingOnError,
  isDone = everyValueTruthy,
  onStop,
  getAttemptCount,
  getStartTime,
}: RefetchIntervalOptions<TData>): (query: RefetchQuery<TData>) => number | false {
  return (query: RefetchQuery<TData>) => {
    if (query.state.data !== undefined && isDone(query.state.data)) {
      onStop?.();
      return false;
    }

    if (maxAttempts !== undefined) {
      if (!query.state.error || getAttemptCount() >= maxAttempts) {
        onStop?.();
        return false;
      }
    } else if (maxDurationMs !== undefined) {
      if (query.state.error) {
        onStop?.();
        return false;
      }
      // Guard against startTime being uninitialized (0) — can happen if refetchInterval
      // fires from cached data before the useEffect above has run.
      const startTime = getStartTime();
      if (startTime > 0 && Date.now() - startTime >= maxDurationMs) {
        onStop?.();
        return false;
      }
    } else if (query.state.error && !keepPollingOnError) {
      // Keep polling through transient errors when opted in; a later poll recovers on its own.
      // Reporting the failure is the query cache's job — see report-poll-error.
      onStop?.();
      return false;
    }

    return POLLING_INTERVAL_MS;
  };
}
